import Vue from 'vue';
import fileUpload from '@/utils/fileUpload';
import { UPLOAD_THRESHOLDS } from '@/utils/uploadHelpers';

// Save uploads to localStorage - persist all uploads during normal operation
const saveUploadsToStorage = uploads => {
  try {
    // Save all uploads during normal operation
    // Filtering only happens when loading from storage on page reload
    localStorage.setItem('upload-progress', JSON.stringify(uploads));
  } catch (error) {
    console.error('Error saving upload progress to localStorage:', error);
  }
};

// Load uploads from localStorage on initialization
const loadUploadsFromStorage = () => {
  try {
    const stored = localStorage.getItem('upload-progress');
    if (!stored) return [];

    const parsedUploads = JSON.parse(stored);
    const now = Date.now();
    const FIFTEEN_MINUTES = 15 * 60 * 1000; // 15 minutes in milliseconds

    // Filter out expired uploads, completed uploads, failed uploads, and validate structure
    const validUploads = parsedUploads.filter(upload => {
      // Validate upload structure
      if (!upload || !upload.id || !upload.fileName) {
        return false;
      }

      // Remove uploads that are too old (avoid stale uploads)
      if (upload.startTime) {
        const uploadTime = new Date(upload.startTime).getTime();
        if (now - uploadTime > FIFTEEN_MINUTES) {
          return false; // Remove old uploads
        }
      }

      // On page reload, remove completed uploads (100% progress)
      if (upload.progress >= 100) {
        return false;
      }

      // On page reload, remove failed uploads
      if (upload.status === 'error') {
        return false;
      }

      // Only restore uploads that are still in progress and in seamless phase
      return upload.progress >= UPLOAD_THRESHOLDS.SEAMLESS_PHASE && upload.progress < 100;
    });

    return validUploads;
  } catch (error) {
    console.error('Error loading upload progress from localStorage:', error);
    return [];
  }
};

// Default state factory
const defaultState = () => {
  const loadedUploads = loadUploadsFromStorage(); // Load from localStorage

  // Auto-show dialog if there are ongoing uploads (not completed, not failed)
  const hasOngoingUploads = loadedUploads.length > 0;

  // Check if dialog was permanently dismissed
  const isDismissed = localStorage.getItem('upload-dialog-dismissed') === 'true';

  return {
    isVisible: hasOngoingUploads && !isDismissed, // Auto-show if there are ongoing uploads and not dismissed
    uploads: loadedUploads
  };
};

const state = defaultState();

const getters = {
  isVisible: state => state.isVisible && state.uploads.length > 0,
  uploads: state => state.uploads,
  activeUploads: state => state.uploads.filter(upload => upload.progress < 100),
  completedUploads: state => state.uploads.filter(upload => upload.progress >= 100),
  errorUploads: state => state.uploads.filter(upload => upload.status === 'error'),
  hasActiveUploads: state => state.uploads.some(upload => upload.progress < 100 && upload.status !== 'error'),
  totalActiveUploads: state => state.uploads.filter(upload => upload.progress < 100 && upload.status !== 'error').length,
  uploadsNeedingCheck: state => state.uploads.filter(upload => upload.realId && upload.status !== 'error' && upload.progress < 100)
};

const mutations = {
  resetState(state) {
    Object.assign(state, defaultState());
  },

  SET_VISIBLE(state, visible) {
    state.isVisible = visible;
  },

  ADD_UPLOADS(state, uploads) {
    state.uploads.push(...uploads);
    saveUploadsToStorage(state.uploads); // Persist to localStorage
  },

  UPDATE_UPLOAD(state, { id, data }) {
    const uploadIndex = state.uploads.findIndex(upload => upload.id === id);
    if (uploadIndex !== -1) {
      // Use Vue.set for reactivity in Vue 2
      Vue.set(state.uploads, uploadIndex, { ...state.uploads[uploadIndex], ...data });
      saveUploadsToStorage(state.uploads); // Persist to localStorage
    }
  },

  UPDATE_UPLOAD_BATCH(state, updates) {
    updates.forEach(({ id, data }) => {
      const uploadIndex = state.uploads.findIndex(upload => upload.id === id);
      if (uploadIndex !== -1) {
        // Use Vue.set for reactivity in Vue 2
        Vue.set(state.uploads, uploadIndex, { ...state.uploads[uploadIndex], ...data });
      }
    });
    saveUploadsToStorage(state.uploads); // Persist to localStorage after batch update
  },

  REMOVE_UPLOAD(state, id) {
    const index = state.uploads.findIndex(upload => upload.id === id);
    if (index !== -1) {
      // Clean up preview URL if it exists
      const upload = state.uploads[index];
      if (upload.previewUrl) {
        URL.revokeObjectURL(upload.previewUrl);
      }
      state.uploads.splice(index, 1);
      saveUploadsToStorage(state.uploads); // Persist to localStorage

      // Auto-hide dialog if no uploads remain
      if (state.uploads.length === 0) {
        state.isVisible = false;
      }
    }
  },

  CLEAR_COMPLETED_UPLOADS(state) {
    const completedUploads = state.uploads.filter(upload => upload.progress >= 100);
    completedUploads.forEach(upload => {
      if (upload.previewUrl) {
        URL.revokeObjectURL(upload.previewUrl);
      }
    });
    state.uploads = state.uploads.filter(upload => upload.progress < 100);
    saveUploadsToStorage(state.uploads); // Persist to localStorage

    // Auto-hide dialog if no uploads remain
    if (state.uploads.length === 0) {
      state.isVisible = false;
    }
  },

  CLEAR_ALL_UPLOADS(state) {
    // Clean up preview URLs before clearing
    state.uploads.forEach(upload => {
      if (upload.previewUrl) {
        URL.revokeObjectURL(upload.previewUrl);
      }
    });
    state.uploads = [];
    saveUploadsToStorage(state.uploads); // Persist to localStorage (will save empty array)

    // Auto-hide dialog when all uploads are cleared
    state.isVisible = false;
  },

  // Cancel all uploads and permanently dismiss dialog
  CANCEL_ALL_UPLOADS(state) {
    // Mark all active uploads as cancelled
    state.uploads.forEach(upload => {
      if (upload.progress < 100 && upload.status !== 'error') {
        Vue.set(upload, 'status', 'cancelled');
        Vue.set(upload, 'message', 'Upload cancelled by user');
        Vue.set(upload, 'progress', 0); // Reset progress to 0 for cancelled uploads
      }
    });

    // Clean up preview URLs for cancelled uploads
    state.uploads.forEach(upload => {
      if (upload.previewUrl) {
        URL.revokeObjectURL(upload.previewUrl);
      }
    });

    // Clear all uploads since they're cancelled
    state.uploads = [];
    saveUploadsToStorage(state.uploads); // Clear localStorage

    // Hide dialog and mark as permanently dismissed
    state.isVisible = false;
    localStorage.setItem('upload-dialog-dismissed', 'true');
  }
};

const actions = {
  resetState({ commit }) {
    commit('resetState');
  },

  // Show the upload progress dialog
  showDialog({ commit }) {
    commit('SET_VISIBLE', true);
  },

  // Hide the upload progress dialog
  hideDialog({ commit }) {
    commit('SET_VISIBLE', false);
  },

  // Add new uploads to the system
  addUploads({ commit }, files) {
    // Clear dismissal when new uploads are added
    localStorage.removeItem('upload-dialog-dismissed');

    const uploads = files.map(file => {
      // Detect file type and extension
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.split('.').pop()?.toUpperCase() || 'FILE';

      let contentType = 'image';
      let iconName = 'image';
      let previewUrl = null;

      if (file.type.startsWith('video/') || fileName.endsWith('.mp4') || fileName.endsWith('.mov') || fileName.endsWith('.mpeg')) {
        contentType = 'video';
        iconName = 'videocam';
      } else if (fileName.endsWith('.pdf') || fileName.endsWith('.doc') || fileName.endsWith('.docx')) {
        contentType = 'document';
        iconName = 'description';
      } else {
        // Generate preview URL for images immediately (fast)
        previewUrl = URL.createObjectURL(file);
      }

      return {
        id: `temp_${Date.now()}_${Math.random()}`,
        fileName: file.name,
        fileExtension,
        size: file.size,
        contentType,
        iconName,
        previewUrl,
        progress: 0,
        status: 'uploading',
        message: '',
        realId: null,
        canDelete: false, // Will be set to true after successful upload
        startTime: new Date().toISOString() // Add timestamp for cleanup
      };
    });

    // Add uploads immediately without waiting for thumbnails
    commit('ADD_UPLOADS', uploads);
    commit('SET_VISIBLE', true);

    // Generate video thumbnails asynchronously for video uploads after they're added to store
    uploads.forEach(upload => {
      if (upload.contentType === 'video') {
        // Find the original file for this upload
        const originalFile = files.find(f => f.name === upload.fileName);
        if (originalFile) {
          fileUpload
            .generateVideoThumbnailAsync(originalFile)
            .then(thumbnailUrl => {
              // Use proper mutation to update the upload with thumbnail
              commit('UPDATE_UPLOAD', {
                id: upload.id,
                data: { previewUrl: thumbnailUrl }
              });
            })
            .catch(error => {
              console.warn('Failed to generate video thumbnail:', error);
            });
        }
      }
    });

    return uploads;
  },

  // Update a single upload
  updateUpload({ commit }, { id, data }) {
    commit('UPDATE_UPLOAD', { id, data });
  },

  // Update an upload with its real content ID after successful upload
  updateUploadWithRealId({ commit }, { tempId, realId, data = {} }) {
    commit('UPDATE_UPLOAD', {
      id: tempId,
      data: {
        realId,
        canDelete: true, // Enable deletion after successful upload
        ...data
      }
    });
  },

  // Update multiple uploads at once from API response (used by TanStack Query)
  updateUploadsFromStatus({ commit, state }, statusData) {
    const updates = statusData
      .map(item => {
        // Find the upload by realId since API returns real content IDs
        const matchingUpload = state.uploads.find(upload => upload.realId === item.id);

        if (!matchingUpload) {
          return null;
        }

        const updateData = {
          progress: item.progress || 0,
          status: item.status,
          message: item.message || '',
          state: item.state
        };

        // For documents, store temporary processing status data (like videos)
        if (matchingUpload.contentType === 'document') {
          updateData.processingStatusData = {
            placeholder_url: item.placeholder_url,
            preview_url: item.preview_url,
            optimized_url: item.optimized_url,
            processing_status: item.status
          };
        }

        return {
          id: matchingUpload.id,
          data: updateData
        };
      })
      .filter(Boolean); // Remove null entries

    commit('UPDATE_UPLOAD_BATCH', updates);
  },

  // Get processing status data for TanStack Query
  async getProcessingStatus({ state }) {
    // Use the SAME filtering logic as GlobalUploadProgress.vue
    const uploadsNeedingCheck = state.uploads.filter(upload => {
      // Must have realId and not be in error state
      if (!upload.realId || upload.status === 'error') {
        return false;
      }

      // For documents: keep polling even at 100% progress until processing is complete
      if (upload.contentType === 'document') {
        const isProcessingComplete = upload.state === 'process_document' && upload.processingStatusData?.processing_status === 'completed';
        return !isProcessingComplete;
      }

      // For images and videos: use original logic (exclude when progress >= 100)
      return upload.progress < 100;
    });

    if (uploadsNeedingCheck.length === 0) {
      return { images: [], videos: [], documents: [] };
    }

    // Group uploads by type for API calls
    const imageIds = uploadsNeedingCheck.filter(upload => upload.contentType === 'image').map(upload => upload.realId);
    const videoIds = uploadsNeedingCheck.filter(upload => upload.contentType === 'video').map(upload => upload.realId);

    // For documents, only check those that don't have completed processing status data
    const documentIds = uploadsNeedingCheck
      .filter(upload => {
        if (upload.contentType !== 'document') return false;

        // Skip documents that already have completed processing status
        const processingData = upload.processingStatusData;
        const isCompletelyProcessed = upload.state === 'process_document' && processingData?.processing_status === 'completed';
        return !isCompletelyProcessed;
      })
      .map(upload => upload.realId);

    const results = { images: [], videos: [], documents: [] };

    try {
      // Import ContentApi dynamically to avoid circular dependencies
      const { ContentApi } = await import('@/api/modules/content');

      // Fetch image processing status
      if (imageIds.length > 0) {
        const imageResponse = await ContentApi.getContentProcessingStatus({ contentIds: imageIds, type: 'image' });
        if (imageResponse.statusCode === 200 && imageResponse.data) {
          results.images = imageResponse.data;
        }
      }

      // Fetch video processing status
      if (videoIds.length > 0) {
        const videoResponse = await ContentApi.getContentProcessingStatus({ contentIds: videoIds, type: 'video' });
        if (videoResponse.statusCode === 200 && videoResponse.data) {
          results.videos = videoResponse.data;
        }
      }

      // Fetch document processing status (only for documents that still need checking)
      if (documentIds.length > 0) {
        const documentResponse = await ContentApi.getContentProcessingStatus({ contentIds: documentIds, type: 'document' });
        if (documentResponse.statusCode === 200 && documentResponse.data) {
          results.documents = documentResponse.data;
        }
      }
    } catch (error) {
      console.error('Error fetching processing status:', error);
    }

    return results;
  },

  // Remove a single upload
  removeUpload({ commit }, id) {
    commit('REMOVE_UPLOAD', id);
  },

  // Clear completed uploads
  clearCompleted({ commit }) {
    commit('CLEAR_COMPLETED_UPLOADS');
  },

  // Clear all uploads
  clearAll({ commit }) {
    commit('CLEAR_ALL_UPLOADS');
  },

  // Cancel all uploads and permanently dismiss dialog
  cancelAllUploads({ commit, state }) {
    // Cancel any ongoing file uploads by aborting XHR requests
    // This will actually abort the network requests in progress
    const actualCancelledCount = fileUpload.cancelAllUploads();

    commit('CANCEL_ALL_UPLOADS');

    // Return cancelled uploads count for user feedback
    const uiCancelledCount = state.uploads.filter(upload => upload.progress < 100 && upload.status !== 'error').length;

    console.log(`Actually cancelled ${actualCancelledCount} active network requests`);
    console.log(`UI cancelled ${uiCancelledCount} upload items`);

    return Math.max(actualCancelledCount, uiCancelledCount);
  },

  // Reset dismissal state (for debugging or if user wants to re-enable)
  resetDismissal() {
    localStorage.removeItem('upload-dialog-dismissed');
  },

  // Show the dialog and clear dismissal (used when new uploads start)
  showDialogAndClearDismissal({ commit }) {
    localStorage.removeItem('upload-dialog-dismissed');
    commit('SET_VISIBLE', true);
  },

  // Check if dialog should be hidden (only on page reload when no ongoing uploads)
  checkAndHideIfAllComplete({ commit, state }) {
    // Only hide if there are no ongoing uploads (all are complete or failed)
    const hasOngoingUploads = state.uploads.some(upload => upload.progress < 100 && upload.status !== 'error');

    if (!hasOngoingUploads && state.uploads.length === 0) {
      // No uploads at all, hide dialog
      commit('SET_VISIBLE', false);
    }
    // If there are completed/failed uploads but no ongoing ones, keep dialog visible
    // so user can see the results and manually close if desired
  },

  // Delete uploaded content
  async deleteUpload({ commit }, { uploadId, realId, contentType }) {
    try {
      // Import ContentApi dynamically to avoid circular dependencies
      const { ContentApi } = await import('@/api/modules/content');

      // Delete the content from the server
      await ContentApi.deleteImage({ content: contentType, id: realId });

      // Remove from store
      commit('REMOVE_UPLOAD', uploadId);

      // Import and use query client to invalidate content queries
      // This ensures ImagesGrid and other components refresh their data
      try {
        const { useQueryClient } = await import('@tanstack/vue-query');
        const queryClient = useQueryClient();

        // Invalidate content queries so ImagesGrid refreshes
        queryClient.invalidateQueries({ queryKey: ['content'] });
        queryClient.invalidateQueries({ queryKey: ['collections'] });
      } catch (queryError) {
        // Fallback: emit a global event that components can listen to
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(
            new CustomEvent('content-deleted', {
              detail: { contentId: realId, contentType }
            })
          );
        }
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
